/**
 * Onglet "Flyers" du back-office B2B.
 *
 * - Upload (PDF / JPG / PNG / WEBP, max 15 MB) avec titre + description + tags
 * - Grille des flyers avec filtre par tag + recherche texte
 * - Actions par flyer : aperçu (modal), télécharger, envoyer par email, supprimer
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../../components/ui/dialog';
import { Badge } from '../../../components/ui/badge';
import {
  Upload, FileText, Image as ImageIcon, Download, Send, Eye, Trash2,
  Plus, Search, X, Tag as TagIcon,
} from 'lucide-react';
import api from '../../../api';
import { toast } from 'sonner';

const MAX_SIZE_MB = 15;
const ALLOWED_MIMES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_EXT_LABEL = 'PDF, JPG, PNG, WEBP';

const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} ko`;
  return `${(bytes / 1024 / 1024).toFixed(2)} Mo`;
};

const isImage = (mime) => mime && mime.startsWith('image/');

export const B2BFlyersTab = () => {
  const [flyers, setFlyers] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [q, setQ] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [loading, setLoading] = useState(false);

  // Upload
  const [uploadOpen, setUploadOpen] = useState(false);
  const [upForm, setUpForm] = useState({ title: '', description: '', tags: '', file: null });
  const [uploading, setUploading] = useState(false);

  // Preview
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFlyer, setPreviewFlyer] = useState(null);

  // Envoi
  const [sendOpen, setSendOpen] = useState(false);
  const [sendFlyer, setSendFlyer] = useState(null);
  const [sendForm, setSendForm] = useState({ to_email: '', subject: '', body: '', cc: '' });
  const [sending, setSending] = useState(false);
  const [clients, setClients] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.b2bListFlyers({ q, tag: tagFilter });
      setFlyers(r.items || []);
      setAllTags(r.all_tags || []);
    } catch (e) {
      toast.error('Erreur chargement flyers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, tagFilter]);

  // Charger les clients à la demande (pour le dialog d'envoi)
  const loadClientsOnce = async () => {
    if (clients.length > 0) return;
    try {
      const r = await api.b2bListClients();
      setClients(r.items || []);
    } catch (e) { /* silent */ }
  };

  const openUpload = () => {
    setUpForm({ title: '', description: '', tags: '', file: null });
    setUploadOpen(true);
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!ALLOWED_MIMES.includes(file.type)) {
      toast.error(`Type non autorisé. Formats acceptés : ${ALLOWED_EXT_LABEL}`);
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`Fichier trop volumineux (${(file.size / 1024 / 1024).toFixed(1)} Mo, max ${MAX_SIZE_MB} Mo)`);
      return;
    }
    // Auto-remplir le titre depuis le nom si vide
    setUpForm((prev) => ({
      ...prev,
      file,
      title: prev.title || file.name.replace(/\.[^.]+$/, ''),
    }));
  };

  const handleUpload = async () => {
    if (!upForm.file) {
      toast.error('Sélectionnez un fichier');
      return;
    }
    if (!upForm.title || upForm.title.trim().length < 2) {
      toast.error('Le titre du flyer est obligatoire (min 2 caractères)');
      return;
    }
    setUploading(true);
    try {
      await api.b2bUploadFlyer({
        title: upForm.title.trim(),
        description: upForm.description.trim(),
        tags: upForm.tags.trim(),
        file: upForm.file,
      });
      toast.success('Flyer uploadé avec succès');
      setUploadOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'Échec upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (flyer) => {
    if (!window.confirm(`Supprimer définitivement le flyer "${flyer.title}" ?`)) return;
    try {
      await api.b2bDeleteFlyer(flyer.id);
      toast.success('Flyer supprimé');
      await load();
    } catch (e) {
      toast.error(e.message || 'Suppression refusée');
    }
  };

  const handlePreview = async (flyer) => {
    try {
      const url = await api.b2bPreviewFlyer(flyer.id);
      setPreviewUrl(url);
      setPreviewFlyer(flyer);
    } catch (e) {
      toast.error('Échec ouverture aperçu');
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewFlyer(null);
  };

  const handleDownload = async (flyer) => {
    try {
      const { blob, filename } = await api.b2bDownloadFlyer(flyer.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Échec téléchargement');
    }
  };

  const openSend = (flyer) => {
    loadClientsOnce();
    setSendFlyer(flyer);
    setSendForm({
      to_email: '',
      subject: `${flyer.title} — Code 2 Roues`,
      body: '',
      cc: '',
    });
    setSendOpen(true);
  };

  const handleSend = async () => {
    if (!sendForm.to_email || !sendForm.to_email.includes('@')) {
      toast.error('Email destinataire invalide');
      return;
    }
    setSending(true);
    try {
      const r = await api.b2bSendFlyer(sendFlyer.id, sendForm);
      toast.success(`Flyer envoyé à ${r.to}`);
      setSendOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'Échec envoi');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="b2b-flyers-tab">
      {/* Toolbar : recherche + filtre tags + bouton upload */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap items-center gap-2 flex-1">
          <div className="relative max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un flyer…"
              className="pl-9"
              data-testid="b2b-flyers-search"
            />
          </div>
          {allTags.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              <TagIcon size={13} className="text-muted-foreground" />
              <Button
                size="sm"
                variant={tagFilter === '' ? 'default' : 'outline'}
                onClick={() => setTagFilter('')}
                className="h-7 text-xs"
              >
                Tous
              </Button>
              {allTags.map((t) => (
                <Button
                  key={t}
                  size="sm"
                  variant={tagFilter === t ? 'default' : 'outline'}
                  onClick={() => setTagFilter(t === tagFilter ? '' : t)}
                  className="h-7 text-xs"
                  data-testid={`b2b-flyers-tag-${t}`}
                >
                  {t}
                </Button>
              ))}
            </div>
          )}
        </div>
        <Button onClick={openUpload} data-testid="b2b-flyers-upload-btn">
          <Plus size={16} className="mr-1.5" /> Nouveau flyer
        </Button>
      </div>

      {/* Grille des flyers */}
      {loading && flyers.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Chargement…</div>
      ) : flyers.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Upload size={32} className="mx-auto text-muted-foreground mb-3" />
            <div className="text-sm text-muted-foreground">Aucun flyer pour le moment.</div>
            <Button className="mt-4" onClick={openUpload}>
              <Plus size={16} className="mr-1.5" />Uploader mon premier flyer
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {flyers.map((f) => {
            const FileIcon = isImage(f.mime) ? ImageIcon : FileText;
            return (
              <Card key={f.id} data-testid={`b2b-flyer-${f.id}`} className="overflow-hidden">
                <button
                  type="button"
                  onClick={() => handlePreview(f)}
                  className="block w-full aspect-[4/3] bg-muted/40 border-b border-border hover:bg-muted/60 transition-colors group"
                  title="Aperçu"
                >
                  <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground group-hover:text-foreground">
                    <FileIcon size={40} className="mb-2" />
                    <span className="text-xs font-bold uppercase">{f.mime?.split('/')[1]}</span>
                    <span className="text-[10px] mt-1">{fmtSize(f.size)}</span>
                  </div>
                </button>
                <CardContent className="p-3">
                  <h3 className="font-black text-sm truncate" title={f.title}>{f.title}</h3>
                  {f.description && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{f.description}</p>
                  )}
                  {f.tags && f.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {f.tags.map((t) => (
                        <Badge key={t} variant="secondary" className="text-[9px] py-0 px-1.5">{t}</Badge>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 text-[10px] text-muted-foreground">
                    {f.sent_count > 0 ? <>Envoyé {f.sent_count}×</> : 'Jamais envoyé'}
                  </div>
                  <div className="flex items-center gap-1 mt-3 pt-3 border-t border-border">
                    <Button size="sm" variant="ghost" className="flex-1 h-8" onClick={() => handlePreview(f)} data-testid={`b2b-flyer-preview-${f.id}`}>
                      <Eye size={13} className="mr-1" /> Aperçu
                    </Button>
                    <Button size="sm" variant="ghost" className="flex-1 h-8" onClick={() => handleDownload(f)} data-testid={`b2b-flyer-download-${f.id}`}>
                      <Download size={13} className="mr-1" /> DL
                    </Button>
                    <Button size="sm" className="flex-1 h-8" onClick={() => openSend(f)} data-testid={`b2b-flyer-send-${f.id}`}>
                      <Send size={13} className="mr-1" /> Envoyer
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => handleDelete(f)} data-testid={`b2b-flyer-delete-${f.id}`} title="Supprimer">
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* DIALOG UPLOAD */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-lg" data-testid="b2b-flyer-upload-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau flyer</DialogTitle>
            <DialogDescription>
              {ALLOWED_EXT_LABEL} — max {MAX_SIZE_MB} Mo
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Fichier *</Label>
              <label
                className="mt-1 flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5 transition-colors"
                data-testid="b2b-flyer-upload-dropzone"
              >
                <Upload size={24} className="text-muted-foreground mb-2" />
                {upForm.file ? (
                  <div className="text-center">
                    <div className="text-sm font-bold">{upForm.file.name}</div>
                    <div className="text-xs text-muted-foreground">{fmtSize(upForm.file.size)} · {upForm.file.type}</div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="text-sm font-bold">Cliquer pour sélectionner</div>
                    <div className="text-xs text-muted-foreground">{ALLOWED_EXT_LABEL} · max {MAX_SIZE_MB} Mo</div>
                  </div>
                )}
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
                  onChange={(e) => handleFileSelect(e.target.files?.[0])}
                />
              </label>
            </div>
            <div>
              <Label htmlFor="up-title">Titre *</Label>
              <Input
                id="up-title"
                value={upForm.title}
                onChange={(e) => setUpForm({ ...upForm, title: e.target.value })}
                placeholder="Plaquette commerciale 2026"
                data-testid="b2b-flyer-upload-title"
              />
            </div>
            <div>
              <Label htmlFor="up-desc">Description</Label>
              <Textarea
                id="up-desc"
                rows={2}
                value={upForm.description}
                onChange={(e) => setUpForm({ ...upForm, description: e.target.value })}
                placeholder="Présentation des prestations B2B Code 2 Roues"
              />
            </div>
            <div>
              <Label htmlFor="up-tags">Tags (séparés par virgule)</Label>
              <Input
                id="up-tags"
                value={upForm.tags}
                onChange={(e) => setUpForm({ ...upForm, tags: e.target.value })}
                placeholder="plaquette, B2B, 2026"
                data-testid="b2b-flyer-upload-tags"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>Annuler</Button>
            <Button onClick={handleUpload} disabled={uploading || !upForm.file} data-testid="b2b-flyer-upload-confirm">
              {uploading ? 'Upload…' : 'Uploader'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG PREVIEW */}
      <Dialog open={!!previewUrl} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col" data-testid="b2b-flyer-preview-dialog">
          <DialogHeader>
            <DialogTitle>{previewFlyer?.title || 'Aperçu'}</DialogTitle>
            <DialogDescription>{previewFlyer?.original_filename}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-muted/40 rounded-lg overflow-hidden">
            {previewUrl && (
              isImage(previewFlyer?.mime) ? (
                <img src={previewUrl} alt={previewFlyer?.title} className="w-full h-full object-contain" />
              ) : (
                <iframe src={previewUrl} title="Aperçu" className="w-full h-full border-0" />
              )
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDownload(previewFlyer)}>
              <Download size={14} className="mr-1.5" /> Télécharger
            </Button>
            <Button onClick={() => { closePreview(); openSend(previewFlyer); }}>
              <Send size={14} className="mr-1.5" /> Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG ENVOI */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-2xl" data-testid="b2b-flyer-send-dialog">
          <DialogHeader>
            <DialogTitle>Envoyer "{sendFlyer?.title}" par email</DialogTitle>
            <DialogDescription>Le fichier sera attaché à l'email envoyé via Resend.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {clients.length > 0 && (
              <div>
                <Label>Choisir un client (optionnel)</Label>
                <select
                  className="mt-1 w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  onChange={(e) => {
                    const id = e.target.value;
                    const c = clients.find((x) => x.id === id);
                    if (c) {
                      setSendForm((prev) => ({
                        ...prev,
                        to_email: c.email || prev.to_email,
                      }));
                    }
                  }}
                  defaultValue=""
                  data-testid="b2b-flyer-send-client"
                >
                  <option value="">— Saisie manuelle —</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id} disabled={!c.email}>
                      {c.name}{c.email ? ` · ${c.email}` : ' (pas d\'email)'}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <Label>Destinataire *</Label>
              <Input
                type="email"
                value={sendForm.to_email}
                onChange={(e) => setSendForm({ ...sendForm, to_email: e.target.value })}
                placeholder="contact@auto-ecole.fr"
                data-testid="b2b-flyer-send-to"
              />
            </div>
            <div>
              <Label>CC (optionnel)</Label>
              <Input
                type="email"
                value={sendForm.cc}
                onChange={(e) => setSendForm({ ...sendForm, cc: e.target.value })}
                placeholder="Pour vous mettre en copie"
              />
            </div>
            <div>
              <Label>Sujet</Label>
              <Input
                value={sendForm.subject}
                onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })}
                data-testid="b2b-flyer-send-subject"
              />
            </div>
            <div>
              <Label>Message (HTML autorisé, laisser vide pour message standard)</Label>
              <Textarea
                rows={5}
                value={sendForm.body}
                onChange={(e) => setSendForm({ ...sendForm, body: e.target.value })}
                placeholder="Laisser vide pour utiliser le message par défaut."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} disabled={sending}>Annuler</Button>
            <Button onClick={handleSend} disabled={sending} data-testid="b2b-flyer-send-confirm">
              <Send size={14} className="mr-1.5" />
              {sending ? 'Envoi…' : 'Envoyer maintenant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
